import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { getPlugins } from '../services/PluginService';
import AuthContext from './AuthContext';
import PlatformBridge from '../utils/PlatformBridge';

const PluginsContext = createContext();

const loadPluginsFromStorage = () => {
  try {
    const stored = localStorage.getItem('plugins');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (error) {
    console.error('Error loading plugins from localStorage:', error);
  }
  return [];
};

export const PluginsProvider = ({ children }) => {
  const { accessToken } = useContext(AuthContext);

  const [plugins, setPlugins] = useState(loadPluginsFromStorage);
  const [isPluginsLoaded, setIsPluginsLoaded] = useState(() => loadPluginsFromStorage().length > 0);

  const fetchPlugins = useCallback(async () => {
    try {
      const response = await getPlugins();
      if (Array.isArray(response)) {
        setPlugins(response);
        localStorage.setItem('plugins', JSON.stringify(response));
        setIsPluginsLoaded(true);
        return response;
      }
    } catch (error) {
      console.error('Error fetching plugins:', error);
      // Si falla, se mantiene lo que ya había en localStorage/estado (no bloquear el dashboard por esto).
    }
    return null;
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const storedAccessToken = await PlatformBridge.getToken('accessToken');
      if (!active) return;
      const hasToken = accessToken || storedAccessToken;

      if (!hasToken) {
        setPlugins([]);
        setIsPluginsLoaded(false);
        localStorage.removeItem('plugins');
        return;
      }

      fetchPlugins();
    })();
    return () => {
      active = false;
    };
  }, [accessToken, fetchPlugins]);

  // Set habilitado para lookups O(1) en hasPlugin, sin recomputar en cada render.
  const enabledKeys = useMemo(
    () => new Set(plugins.filter((plugin) => plugin.enabled).map((plugin) => plugin.key)),
    [plugins]
  );

  // Mientras no se cargó el catálogo todavía (ej. primer login, antes de la primera respuesta),
  // no hay que ocultar nada por las dudas: se asume habilitado hasta tener certeza.
  const hasPlugin = useCallback(
    (key) => (!isPluginsLoaded ? true : enabledKeys.has(key)),
    [isPluginsLoaded, enabledKeys]
  );

  return (
    <PluginsContext.Provider value={{ plugins, isPluginsLoaded, fetchPlugins, hasPlugin }}>
      {children}
    </PluginsContext.Provider>
  );
};

export default PluginsContext;
